import cn from "classnames"
import { useMemo } from "react"

import { Flex, Skeleton, Tree } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { FileOutlined } from "@/icons"
import { InformationAlert } from "@/molecules/InformationAlert"
import { useVirtualMachineConfig } from "@/organisms/Drawers/applications/inventory/useInventory"
import {
  buildState,
  buildTreeData,
  countSelectedPaths,
  getArtifactSources
} from "@/organisms/Drawers/applications/inventory/useInventory.selectors"
import { ConfigurationBadge } from "@/organisms/Drawers/components/ConfigurationBadge/ConfigurationBadge"
import { PlaceholderConfiguration } from "@/organisms/Drawers/components/PlaceholderConfiguration"
import { Form } from "@/organisms/Form"
import { FooterConfigurationTabVirtualMachineDrawer } from "../FooterConfigurationTabVirtualMachineDrawer"
import { FormConfigurationTabVirtualMachineDrawer } from "../FormConfigurationTabVirtualMachineDrawer"
import * as helpers from "./helpers"

import styles from "./Configuration.module.scss"

import type { TabComponentProps } from "@/organisms/Drawers/components/TabsDrawerTemplate"

export const Configuration = (
  props: TabComponentProps & { footer?: React.ReactNode }
) => {
  const { record } = props
  const { t: tConfiguration } = useTranslation("translation", {
    keyPrefix: "inventory.vm.drawer.configuration"
  })
  const { form, custom } = Form.useForm()
  const recordId = record?.id

  const context = useVirtualMachineConfig(recordId)
  const {
    isPending,
    autoScalingGroup,
    instanceId = "root",
    modules,
    cfg,
    isVmStale
  } = context

  const state = useMemo(() => {
    if (isPending) return buildState([], [])

    return buildState(modules || [], cfg || [])
  }, [modules, cfg, isPending])
  const tree = useMemo(
    () => [
      {
        isInstance: true,
        key: instanceId,
        isLeaf: false,
        checkable: true,
        title: instanceId,
        children: buildTreeData(state)
      }
    ],
    [state, instanceId]
  )
  const pathSelected = Form.useWatch("pathSelected", form) as
    string[] | undefined
  const countSelected = useMemo(
    () => countSelectedPaths(state, pathSelected || []),
    [state, pathSelected]
  )

  const isRootSelected = helpers.isRootSelected(pathSelected, tree)
  const isPathSelected = pathSelected && pathSelected.length > 0
  const projectSelectedByPath = Form.useWatch("projectSelectedByPath", form) as
    boolean | undefined
  const showAlert = isPathSelected && !projectSelectedByPath
  const isAutoScalingGroup = !!autoScalingGroup
  const somePathCorrelatedToProject = Form.useWatch(
    "somePathCorrelatedToProject",
    form
  ) as boolean | undefined

  return (
    <>
      <Flex flex={1}>
        <Flex
          vertical
          gap={16}
          flex={1}
          className={cn(
            styles.section,
            isAutoScalingGroup && styles.sectionWithAlert
          )}
        >
          {isPending && <Skeleton active />}
          {!isPending && (
            <>
              {isAutoScalingGroup && (
                <InformationAlert
                  palette="info"
                  message={tConfiguration("alerts.instanceGroup", {
                    name: autoScalingGroup
                  })}
                />
              )}
              <Tree
                checkable
                disabled={context.isUpdating}
                selectable={false}
                treeData={tree}
                checkedKeys={pathSelected || []}
                defaultExpandedKeys={
                  tree?.length === 1 ? tree.map((item) => item.key) : []
                }
                titleRender={(node: (typeof tree)[number]) => {
                  const currentNode = state.nodes[`${node.key}`]
                  let isCorrelatedToImage = currentNode?.correlated
                  const isStale = currentNode ? !!currentNode.stale : isVmStale

                  if (!currentNode)
                    isCorrelatedToImage =
                      tree?.[0]?.children?.length > 0 &&
                      tree[0].children.every(
                        (child) => state.nodes[`${child.key}`]?.correlated
                      )

                  return (
                    <Flex gap={8} align="center">
                      <Flex gap={4} align="center">
                        {!!currentNode?.isLeaf && <FileOutlined />}
                        <span>{node.title}</span>
                      </Flex>
                      {isStale && <ConfigurationBadge dot type="stale" />}
                      {!isStale && isCorrelatedToImage && (
                        <ConfigurationBadge dot type="vm" />
                      )}
                    </Flex>
                  )
                }}
                onCheck={(_, info) => {
                  if (info.checkedNodesPositions) {
                    const items = helpers.getRootItems(
                      info.checkedNodesPositions
                    )
                    let rootItems = items.map((item) => `${item.node.key}`)
                    const isRootSelected = helpers.isRootSelected(
                      rootItems,
                      tree
                    )

                    rootItems = isRootSelected
                      ? items[0].node.children?.map(
                          (child) => `${child.key}`
                        ) || []
                      : rootItems

                    const byPath: Record<string, string[]> = {}
                    let projects = (cfg || []).map((item) => {
                      rootItems.forEach((path) => {
                        const currentPath = `${path}`

                        return getArtifactSources(
                          item.artifactSources,
                          state
                        )?.some((source) => {
                          let isMatch =
                            source === currentPath ||
                            currentPath.startsWith(`${source}/`) ||
                            source.startsWith(`${currentPath}/`)

                          if (isRootSelected) isMatch = source === currentPath

                          if (!isMatch) return false

                          if (!byPath[currentPath])
                            byPath[currentPath] = [source]
                          else byPath[currentPath].push(source)

                          return isMatch
                        })
                      })

                      return item
                    })

                    const allPaths = Object.keys(byPath).reduce((acc, key) => {
                      const path = byPath[key].filter(
                        (path) =>
                          !byPath[key].some(
                            (s) => s.startsWith(`${path}/`) && key.includes(s)
                          )
                      )

                      return [...acc, ...path]
                    }, [] as string[])
                    projects = projects.filter((item) =>
                      getArtifactSources(item.artifactSources, state)?.some(
                        (source) => allPaths.includes(source)
                      )
                    )

                    const pathWithoutCorrelation = rootItems.some(
                      (path) => !byPath[`${path}`]
                    )
                    const projectCount = projects?.length || 0
                    const projectSelectedByPath =
                      projectCount === 1 && !pathWithoutCorrelation
                    const somePathCorrelatedToProject =
                      (cfg || []).filter((item) =>
                        rootItems.some((path) =>
                          getArtifactSources(item.artifactSources, state)?.some(
                            (source) =>
                              source === path ||
                              `${path}`.startsWith(`${source}/`)
                          )
                        )
                      ).length > 0 && !projectSelectedByPath

                    const isSomePathSelected = rootItems.length > 0
                    const projectValue =
                      projectCount === 0
                        ? form.getFieldValue("project") || undefined
                        : undefined

                    form.setFieldsValue({
                      projectSelectedByPath,
                      somePathCorrelatedToProject,
                      pathSelected: isRootSelected
                        ? items.map((item) => item.node.key)
                        : rootItems,
                      project: projectSelectedByPath
                        ? {
                            label: projects[0].projectName,
                            value: projects[0].codeRepoId,
                            data: projects[0]
                          }
                        : projectValue,
                      correlateToProject: projectSelectedByPath
                        ? projects[0].projectName
                        : projectValue
                    })

                    if (!isSomePathSelected) {
                      form.resetFields([
                        "project",
                        "projectSelectedByPath",
                        "somePathCorrelatedToProject",
                        "pathSelected",
                        "correlateToProject"
                      ])
                      custom.setIsDirty(false)
                    }
                  }
                }}
              />
            </>
          )}
        </Flex>
        <Flex
          flex="1 0 0"
          justify="center"
          className={cn(
            styles.section,
            isAutoScalingGroup && styles.sectionWithAlert
          )}
        >
          {isPending && <Skeleton active />}
          {!isPending && (
            <Flex flex={1} vertical gap={16}>
              {showAlert && !somePathCorrelatedToProject && (
                <InformationAlert
                  palette="info"
                  message={tConfiguration(
                    `alerts.${isRootSelected ? "root" : "path"}`
                  )}
                />
              )}
              {somePathCorrelatedToProject && (
                <InformationAlert
                  palette="info"
                  message={tConfiguration("alerts.correlated")}
                />
              )}
              <FormConfigurationTabVirtualMachineDrawer
                isPathSelected={isPathSelected}
                isRootSelected={isRootSelected}
                context={context}
                tree={tree}
                {...props}
              />
              {!isPathSelected && <PlaceholderConfiguration />}
            </Flex>
          )}
        </Flex>
      </Flex>
      <FooterConfigurationTabVirtualMachineDrawer
        isRootSelected={isRootSelected}
        context={context}
        meta={countSelected}
      />
    </>
  )
}
